import { Component } from "react";
import PropTypes from "prop-types";
import ReactOnRails from "react-on-rails";
import moment from "moment";
import axios from "axios";
import Alert from "@shared/v2/Alert";
import Button from "@shared/v2/Button";
import Dropdown from "@shared/v2/Dropdown";
import Modal from "@shared/v2/Modal";
import TextInput from "@shared/v2/TextInput";
import Typography from "@shared/v2/Typography";
import serialize from "form-serialize";
import JobRunner from "./JobRunner";
import OktaStatus from "./OktaStatus";
import UserTable from "./UserTable";

const TRUE_FALSE_OPTIONS = [
  { value: true, label: "True" },
  { value: false, label: "False" },
];

const optionize = (options) =>
  [{ value: "", label: "" }].concat(options.map((o) => ({ value: o, label: o })));

class SupportUsersIndex extends Component {
  constructor(props) {
    super(props);

    this.state = {
      modalOpen: false,
      isSubmitting: false,
      editUser: null,
      users: props.users,
      userError: false,
      successMessage: null,
    };
  }

  showModal = () => {
    this.setState({ modalOpen: true });
  };

  hideModal = () => {
    this.setState({ modalOpen: false, userError: false, successMessage: null });
  };

  editUserModal = (user) => {
    this.setState({ editUser: user, userError: false });
    this.showModal();
  };

  handleSubmit = (event) => {
    const { editUser } = this.state;
    event.preventDefault();
    this.setState({ isSubmitting: event });
    const formData = serialize(event.target, { hash: true });

    axios
      .post(
        `/support/accounts/${editUser.id}/update_user`,
        { user: formData, authenticity_token: ReactOnRails.authenticityToken() },
        { headers: { "Content-Type": "application/json" } },
      )
      .then((res) => this.userUpdateSuccess(res))
      .catch((error) => {
        this.handleError(error.response.data);
      });
  };

  updateTableRow = (user) => {
    this.setState(({ users }) => ({ users: users.map((u) => (u.id === user.id ? user : u)) }));
  };

  userUpdateSuccess = (response) => {
    this.setState({ isSubmitting: false });
    this.hideModal();
    this.updateTableRow(response.data);
  };

  handleJobSuccess = (message) => {
    this.setState({ successMessage: message });
  };

  handleError = (error) => {
    this.setState({ isSubmitting: false, userError: error });
  };

  render() {
    const {
      current_super_user_role: currentSuperUserRole,
      person_types: personTypes,
      person_statuses: personStatuses,
    } = this.props;
    const { editUser, isSubmitting, modalOpen, successMessage, userError, users } = this.state;
    return (
      <>
        <UserTable
          currentSuperUserRole={currentSuperUserRole}
          onEditUser={this.editUserModal}
          users={users}
        />

        <Modal
          show={modalOpen}
          onHide={this.hideModal}
          dialogClassName="tw-flex tw-items-center tw-justify-center tw-h-full"
          contentClassName="tw-max-w-[1000px] tw-max-h-[90vh] tw-w-full tw-flex tw-flex-col tw-gap-[32px]"
        >
          <Modal.Header title={`Edit User ${editUser?.id || ""}`} closeButton />

          <form onSubmit={this.handleSubmit} className="tw-flex tw-flex-col tw-gap-[32px] tw-overflow-hidden">
            <Modal.Body className="tw-flex-1 tw-overflow-auto tw-flex tw-flex-col tw-gap-[24px]">
              {userError && <Alert variant="error" text={userError.errors?.map((error) => `${error} `)} />}
              {successMessage && <Alert variant="success" text={successMessage} />}
              {editUser ? (
                <>
                  <div className="tw-flex tw-gap-[8px]">
                    <div className="tw-basis-4/12">
                      <Typography variant="subtitle-m-bold">Created At (PST):</Typography>
                      {moment(editUser.created_at).format("MM/DD/YYYY hh:mm A")}
                    </div>
                    <div className="tw-basis-4/12">
                      <Typography variant="subtitle-m-bold">Updated At (PST):</Typography>
                      {moment(editUser.updated_at).format("MM/DD/YYYY hh:mm A")}
                    </div>
                    <div className="tw-basis-4/12">
                      <OktaStatus accountId={editUser.account_id} userId={editUser.id} />
                    </div>
                  </div>

                  <JobRunner
                    accountId={editUser.account_id}
                    handleSuccess={this.handleJobSuccess}
                    handleError={this.handleError}
                    userId={editUser.id}
                  />

                  <div className="tw-flex tw-gap-[16px]">
                    <div className="tw-basis-1/2 tw-flex tw-flex-col tw-gap-[16px]">
                      <Typography variant="subtitle-m-bold">User Details</Typography>
                      <TextInput name="email" label="Email" defaultValue={editUser.email} />
                      <Dropdown
                        name="active"
                        label="Active"
                        value={TRUE_FALSE_OPTIONS.find((option) => option.value === editUser.active)}
                        onChange={({ value }) => {
                          this.setState(({ editUser: u }) => ({
                            editUser: { ...u, active: value },
                          }));
                        }}
                        options={TRUE_FALSE_OPTIONS}
                        menuShouldComeToFront
                      />
                      <TextInput
                        name="managed_phone_number"
                        label="Managed Phone Number"
                        defaultValue={editUser.managed_phone_number}
                      />
                      <TextInput
                        name="user_lead_parsing_email_attributes[email]"
                        label="Lead Parsing Email"
                        defaultValue={
                          editUser.user_lead_parsing_email ? editUser.user_lead_parsing_email.email : ""
                        }
                      />
                      <Dropdown
                        name="crm_access"
                        label="CRM Access"
                        value={TRUE_FALSE_OPTIONS.find((option) => option.value === editUser.crm_access)}
                        onChange={({ value }) => {
                          this.setState(({ editUser: u }) => ({
                            editUser: { ...u, crm_access: value },
                          }));
                        }}
                        options={TRUE_FALSE_OPTIONS}
                        menuShouldComeToFront
                      />
                    </div>
                    <div className="tw-basis-1/2 tw-flex tw-flex-col tw-gap-[16px]">
                      <Typography variant="subtitle-m-bold">Person Details</Typography>
                      {editUser.person ? (
                        <>
                          <TextInput
                            name="person_attributes[first_name]"
                            label="First Name"
                            defaultValue={editUser.person.first_name}
                          />
                          <TextInput
                            name="person_attributes[last_name]"
                            label="Last Name"
                            defaultValue={editUser.person.last_name}
                          />
                          <Dropdown
                            label="Status"
                            name="person_attributes[status]"
                            value={optionize(personStatuses).find(
                              (option) => option.value === editUser.person.status,
                            )}
                            onChange={({ value }) => {
                              this.setState(({ editUser: u }) => ({
                                editUser: {
                                  ...u,
                                  person: { ...u.person, status: value },
                                },
                              }));
                            }}
                            options={optionize(personStatuses)}
                            menuShouldComeToFront
                          />
                          <Dropdown
                            label="Type Name"
                            name="person_attributes[type_name]"
                            value={optionize(personTypes).find(
                              (option) => option.value === editUser.person.type_name,
                            )}
                            onChange={({ value }) => {
                              this.setState(({ editUser: u }) => ({
                                editUser: {
                                  ...u,
                                  person: { ...u.person, type_name: value },
                                },
                              }));
                            }}
                            options={optionize(personTypes)}
                            menuShouldComeToFront
                          />
                        </>
                      ) : (
                        <div>
                          <h3 className="text-danger">THIS USER IS MISSING A PERSON</h3>
                          <p>
                            This is indicative of an exception elsewhere. Find or create a person to associate
                            with this user.
                            <br />
                            Enter a person UUID below to associate this user to a person.
                          </p>
                          <TextInput name="new_person_uuid" placeholder="Person UUID" />
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                "No User Selected"
              )}
            </Modal.Body>

            <Modal.Footer className="tw-flex tw-justify-between tw-gap-[16px]">
              <Button size="medium" schema="tertiary" onClick={this.hideModal}>
                Cancel
              </Button>

              <Button type="submit" size="medium" isLoading={isSubmitting}>
                Save
              </Button>
            </Modal.Footer>
          </form>
        </Modal>
      </>
    );
  }
}

SupportUsersIndex.propTypes = {
  current_super_user_role: PropTypes.string.isRequired,
  person_types: PropTypes.arrayOf(PropTypes.string).isRequired,
  person_statuses: PropTypes.arrayOf(PropTypes.string).isRequired,
  users: PropTypes.arrayOf(PropTypes.shape()).isRequired,
};

export default SupportUsersIndex;
